/**
 * TopBar — EINZIGE Kopfzeile der Plattform-Shell (Design-Korrektur:
 * ursprünglich zwei Kopfzeilen wie in rmnc-personal geplant/gebaut, das ist
 * dort ein Fehler und wird korrigiert — hier NICHT nachgebaut). Die dunkle
 * Plattform-Leiste trägt alles: links Sidebar-Collapse-Burger + Wortmarke;
 * rechts ⌘K-Suche, AppSwitcher, EnvBadge, Benutzername, Abmelden.
 */
import { Menu, LogOut, Search } from "lucide-react"
import { useAuth } from "@/auth/AuthProvider"
import { AppSwitcher } from "@/components/AppSwitcher"
import { EnvBadge } from "@/components/shared"

interface TopBarProps {
  /** Öffnet die bestehende Command-Palette (⌘K / Strg-K) */
  onOpenPalette?: () => void
  /** Klappt die Sidebar ein/aus */
  onToggleSidebar?: () => void
}

export function TopBar({ onOpenPalette, onToggleSidebar }: TopBarProps) {
  const { user, signOut } = useAuth()

  return (
    <div className="flex h-14 shrink-0 flex-nowrap items-center gap-3 bg-slate-900 px-4 text-white">
      {/* Sidebar-Collapse */}
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label="Sidebar umschalten"
        className="shrink-0 rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Wortmarke — nie gequetscht/abgeschnitten (Optik = Technik-Vorlage) */}
      <a href="/organisationen" className="shrink-0 whitespace-nowrap text-sm font-semibold">
        RM<span style={{ color: "#9b59b6" }}>NC</span> Stammdaten
      </a>

      <div className="flex flex-1 items-center justify-end gap-2">
        {/* Reihenfolge = Technik-Vorlage: EnvBadge → Suche → AppSwitcher → Name → Abmelden */}
        <EnvBadge />

        {/* Such-Trigger — öffnet die bestehende CommandPalette */}
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Suchen oder navigieren"
          className="hidden shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 md:flex"
        >
          <span>Suchen…</span>
          <kbd className="ml-1 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium">⌘K</kbd>
        </button>
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Suchen oder navigieren"
          className="shrink-0 rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 md:hidden"
        >
          <Search className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>

        {/* App-Switcher (9-Punkte-Grid + Panel) */}
        <div className="shrink-0">
          <AppSwitcher currentAppKey="stammdaten" roles={user?.roles ?? []} />
        </div>

        {/* Benutzername */}
        <span className="hidden min-w-0 max-w-[10rem] truncate text-sm text-slate-300 md:inline">
          {user?.name}
        </span>

        {/* Abmelden */}
        <button
          type="button"
          onClick={signOut}
          aria-label="Abmelden"
          className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          <LogOut className="h-4 w-4" />
          <span className="hidden md:inline">Abmelden</span>
        </button>
      </div>
    </div>
  )
}
